import { useState, useEffect, useMemo, useCallback } from "react";
import { Award, XCircle, Smile, Clock, ChevronLeft, ChevronRight } from "lucide-react";
import { apiGet } from "../../utils/api";
import "./analytics.css";
import "./allAttendance.css";

// ─── Constants ────────────────────────────────────────────────────────────────

const MONTH_SHORT = ["Jan","Feb","Mar","Apr","May","Jun","Jul","Aug","Sep","Oct","Nov","Dec"];
const DAY_LABELS  = ["Sun","Mon","Tue","Wed","Thu","Fri","Sat"];

const STATUS_LABEL = {
    weekend:  "Weekend",
    holiday:  "Holiday",
    absent:   "Absent",
    present:  "Present",
    overtime: "Overtime",
};

// ─── Helpers ──────────────────────────────────────────────────────────────────

function toDateStr(date) {
    const y = date.getFullYear();
    const m = String(date.getMonth() + 1).padStart(2, "0");
    const d = String(date.getDate()).padStart(2, "0");
    return `${y}-${m}-${d}`;
}

function formatLong(dateStr) {
    const d = new Date(dateStr + "T00:00:00");
    return d.toLocaleDateString("en-US", { weekday: "long", month: "long", day: "numeric", year: "numeric" });
}

function buildDayCell(cur, attendanceData, todayStr, workingDays = [1,2,3,4,5], holidays = [], overtimeThresholdHrs = 8, dailyOnlineTime = []) {
    const dateStr = toDateStr(cur);
    const dow     = cur.getDay();
    const month   = cur.getMonth();
    const day     = cur.getDate();
    const record  = attendanceData.find(a => a.date === dateStr);
    const isHoliday = holidays.some(h => h.date === dateStr) || record?.isHoliday;

    let hours = 0, lateMin = 0, worked = false;
    if (record && !record.isLeave) {
        if (record.onlineMs) {
            hours = record.onlineMs / 3_600_000;
        } else if (record.checkInTime) {
            // "Still open" (no checkOutTime) only means "ongoing" for TODAY's cell —
            // for any past day, an unwritten checkout is stale data, not proof the
            // session ran until this very moment. Anchoring those to `now` made old
            // records balloon to absurd totals (e.g. 18h) as days went by. Cap a
            // past day at its own end-of-day, and always cap at 24h as a backstop.
            const isRecordToday = dateStr === todayStr;
            const out = record.checkOutTime
                ? new Date(record.checkOutTime)
                : (isRecordToday ? new Date() : new Date(`${dateStr}T23:59:59`));
            hours = Math.min(24, Math.max(0, (out - new Date(record.checkInTime)) / 3_600_000));
        }
        lateMin = record.lateMinutes || 0;
        worked = hours > 0 || !!record.checkInTime;
    } else if (!record) {
        // No formal check-in recorded for this date — fall back to actual tracked
        // online time, same as the HR org-wide attendance calendar already does.
        // Without this, a day the employee genuinely worked (proven by tracked
        // online time) shows "Absent" just because the check-in write happened to
        // fail or get skipped that day.
        const onlineDay = dailyOnlineTime.find(d => d.date === dateStr);
        if (onlineDay?.onlineMs > 0) {
            hours = onlineDay.onlineMs / 3_600_000;
            worked = true;
        }
    }

    // Priority: an explicit holiday flag wins outright; real evidence of work (a
    // check-in or tracked online time) always counts as present/overtime even on
    // a day the schedule marks as a weekend — someone who actually worked a
    // Saturday shouldn't have that day silently swallowed into "weekend" and
    // dropped from both the present and absent tallies.
    let status;
    if (isHoliday) {
        status = "holiday";
    } else if (worked) {
        status = hours > overtimeThresholdHrs ? "overtime" : "present";
    } else if (!workingDays.includes(dow)) {
        status = "weekend";
    } else {
        status = "absent";
    }

    return { dateStr, month, day, dow, status,
        hours: parseFloat(hours.toFixed(1)), lateMin,
        isToday: dateStr === todayStr, isFuture: dateStr > todayStr };
}

function buildWeeks(year, attendanceData, workingDays, holidays, overtimeThresholdHrs, dailyOnlineTime) {
    const todayStr = toDateStr(new Date());
    const jan1  = new Date(year, 0, 1);
    const dec31 = new Date(year, 11, 31);
    const days  = [];
    for (let i = 0; i < jan1.getDay(); i++) days.push(null);
    const cur = new Date(jan1);
    while (cur <= dec31) { days.push(buildDayCell(cur, attendanceData, todayStr, workingDays, holidays, overtimeThresholdHrs, dailyOnlineTime)); cur.setDate(cur.getDate() + 1); }
    const endDow = dec31.getDay();
    if (endDow < 6) for (let i = endDow + 1; i <= 6; i++) days.push(null);
    const weeks = [];
    for (let i = 0; i < days.length; i += 7) weeks.push(days.slice(i, i + 7));
    return weeks;
}

function buildMonthWeeks(year, month, attendanceData, workingDays, holidays, overtimeThresholdHrs, dailyOnlineTime) {
    const todayStr = toDateStr(new Date());
    const first = new Date(year, month, 1);
    const last  = new Date(year, month + 1, 0);
    const days  = [];
    for (let i = 0; i < first.getDay(); i++) days.push(null);
    const cur = new Date(first);
    while (cur <= last) { days.push(buildDayCell(cur, attendanceData, todayStr, workingDays, holidays, overtimeThresholdHrs, dailyOnlineTime)); cur.setDate(cur.getDate() + 1); }
    const endDow = last.getDay();
    if (endDow < 6) for (let i = endDow + 1; i <= 6; i++) days.push(null);
    const weeks = [];
    for (let i = 0; i < days.length; i += 7) weeks.push(days.slice(i, i + 7));
    return weeks;
}

function buildWeekDays(weekStart, attendanceData, workingDays, holidays, overtimeThresholdHrs, dailyOnlineTime) {
    const todayStr = toDateStr(new Date());
    const days = [];
    for (let i = 0; i < 7; i++) {
        const cur = new Date(weekStart);
        cur.setDate(cur.getDate() + i);
        days.push(buildDayCell(cur, attendanceData, todayStr, workingDays, holidays, overtimeThresholdHrs, dailyOnlineTime));
    }
    return days;
}

function getThisWeekStart() {
    const today = new Date();
    const d = new Date(today);
    d.setDate(today.getDate() - today.getDay());
    d.setHours(0, 0, 0, 0);
    return d;
}

function formatWeekRange(ws) {
    const we = new Date(ws);
    we.setDate(we.getDate() + 6);
    const opts = { month: "short", day: "numeric" };
    const sameYear = ws.getFullYear() === we.getFullYear();
    const start = ws.toLocaleDateString("en-US", opts);
    const end   = we.toLocaleDateString("en-US", sameYear ? opts : { ...opts, year: "numeric" });
    return `${start} – ${end}${sameYear ? `, ${ws.getFullYear()}` : ""}`;
}

// ─── Component ────────────────────────────────────────────────────────────────

export default function EmployeeDashboard() {
    const [employee,       setEmployee]       = useState(null);
    const [attendanceData, setAttendanceData] = useState([]);
    const [dailyOnlineTime, setDailyOnlineTime] = useState([]);
    const [workingDays,    setWorkingDays]    = useState([1,2,3,4,5]);
    const [holidays,       setHolidays]       = useState([]);
    const [overtimeThresholdHrs, setOvertimeThresholdHrs] = useState(8);
    const [loading,        setLoading]        = useState(true);
    const [errorMessage,   setErrorMessage]   = useState("");
    const [selectedYear,   setSelectedYear]   = useState(new Date().getFullYear());
    const [selectedMonth,  setSelectedMonth]  = useState(new Date().getMonth());
    const [weekStart,      setWeekStart]      = useState(() => getThisWeekStart());
    const [heatmapView,    setHeatmapView]    = useState("year");
    const [tooltip,        setTooltip]        = useState(null);

    useEffect(() => { fetchData(); }, []);

    async function fetchData() {
        setLoading(true);
        try {
            const res = await apiGet("/api/analytics/my-attendance");
            const result = await res.json().catch(() => null);
            if (res.ok && result?.success) {
                if (result.employee) {
                    setEmployee(result.employee);
                    setAttendanceData(result.employee.attendance || []);
                    setDailyOnlineTime(result.employee.details?.dailyOnlineTime || []);
                    if (result.workingDays) setWorkingDays(result.workingDays);
                    if (result.holidays)    setHolidays(result.holidays);
                    if (Number.isFinite(result.workHoursPerDay) && result.workHoursPerDay > 0) {
                        setOvertimeThresholdHrs(result.workHoursPerDay);
                    }
                } else {
                    setErrorMessage("No attendance data found yet.");
                }
            } else {
                setErrorMessage(result?.message || "Failed to load attendance data");
            }
        } catch (err) {
            console.error("Failed to fetch attendance data:", err);
            setErrorMessage("Failed to load attendance data");
        } finally {
            setLoading(false);
        }
    }

    const yearWeeks     = useMemo(() => buildWeeks(selectedYear, attendanceData, workingDays, holidays, overtimeThresholdHrs, dailyOnlineTime),                    [selectedYear, attendanceData, workingDays, holidays, overtimeThresholdHrs, dailyOnlineTime]);
    const monthWeeks    = useMemo(() => buildMonthWeeks(selectedYear, selectedMonth, attendanceData, workingDays, holidays, overtimeThresholdHrs, dailyOnlineTime), [selectedYear, selectedMonth, attendanceData, workingDays, holidays, overtimeThresholdHrs, dailyOnlineTime]);
    const weekDays      = useMemo(() => buildWeekDays(weekStart, attendanceData, workingDays, holidays, overtimeThresholdHrs, dailyOnlineTime),                    [weekStart, attendanceData, workingDays, holidays, overtimeThresholdHrs, dailyOnlineTime]);
    const yearCalMonths = useMemo(
        () => Array.from({ length: 12 }, (_, mi) => buildMonthWeeks(selectedYear, mi, attendanceData, workingDays, holidays, overtimeThresholdHrs, dailyOnlineTime)),
        [selectedYear, attendanceData, workingDays, holidays, overtimeThresholdHrs, dailyOnlineTime]
    );

    // Days before this device/employee even existed must not count as "Absent" —
    // otherwise the year view (the default) counts every working day back to
    // Jan 1 as absent for anyone who joined partway through the year.
    const earliestDateStr = useMemo(() => {
        const raw = employee?.details?.createdAt;
        if (!raw) return null;
        const d = new Date(raw);
        return Number.isNaN(d.getTime()) ? null : toDateStr(d);
    }, [employee]);

    const summary = useMemo(() => {
        const c = { present: 0, absent: 0, holiday: 0, overtime: 0 };
        const cells = heatmapView === "week"
            ? weekDays
            : (heatmapView === "month" ? monthWeeks.flat() : yearWeeks.flat());
        cells.forEach(d => {
            if (!d || d.isFuture || d.status === "weekend") return;
            if (earliestDateStr && d.dateStr < earliestDateStr) return;
            c[d.status] = (c[d.status] || 0) + 1;
        });
        return c;
    }, [heatmapView, yearWeeks, monthWeeks, weekDays, earliestDateStr]);

    const handleEnter = useCallback((day, e) => {
        if (!day || day.isFuture) return;
        const r = e.currentTarget.getBoundingClientRect();
        setTooltip({ day, x: r.left + r.width / 2, y: r.top });
    }, []);
    const handleLeave = useCallback(() => setTooltip(null), []);

    const stepWeek = (dir) => setWeekStart(prev => {
        const d = new Date(prev);
        d.setDate(d.getDate() + dir * 7);
        return d;
    });

    if (loading) return <div className="emp-loading">Loading your attendance…</div>;
    if (!employee) return (
        <div className="emp-error" style={{ display:"flex", flexDirection:"column", alignItems:"center", justifyContent:"center", minHeight:320, gap:12, padding:40 }}>
            <Clock size={40} style={{ opacity: 0.3 }} />
            <h2 style={{ fontSize:18, fontWeight:600, margin:0 }}>{errorMessage || "No attendance data available"}</h2>
            <p style={{ fontSize:13, opacity:0.6, margin:0, textAlign:"center" }}>
                {errorMessage?.includes("device") || errorMessage?.includes("linked")
                    ? "Ask your tenant admin to link a device to your account to track attendance."
                    : "No attendance records found for your account yet."}
            </p>
        </div>
    );

    const periodBadge = heatmapView === "year"  ? `${selectedYear}`
                      : heatmapView === "month" ? `${MONTH_SHORT[selectedMonth]} ${selectedYear}`
                      : formatWeekRange(weekStart);
    const summaryPeriod = heatmapView === "year"  ? `in ${selectedYear}`
                        : heatmapView === "month" ? `in ${MONTH_SHORT[selectedMonth]}`
                        : "this week";

    return (
        <div className="attendance-view">
            <header className="attendance-header">
                <div className="header-left">
                    <h1>My Attendance</h1>
                    <p>Your check-in/check-out history and attendance summary</p>
                </div>
            </header>

            <div className="summary-cards">
                <div className="summary-card present">
                    <Award size={24} />
                    <div className="card-info">
                        <span className="card-value">{summary.present + summary.overtime}</span>
                        <span className="card-label">Days Present <em className="card-period">{summaryPeriod}</em></span>
                    </div>
                </div>
                <div className="summary-card leave">
                    <XCircle size={24} />
                    <div className="card-info">
                        <span className="card-value">{summary.absent}</span>
                        <span className="card-label">Absent <em className="card-period">{summaryPeriod}</em></span>
                    </div>
                </div>
                <div className="summary-card holiday">
                    <Smile size={24} />
                    <div className="card-info">
                        <span className="card-value">{summary.holiday}</span>
                        <span className="card-label">Holidays <em className="card-period">{summaryPeriod}</em></span>
                    </div>
                </div>
            </div>

            <div className="hm-wrapper">
                {/* ── Title + view tabs ── */}
                <div className="hm-top-row">
                    <h3 className="hm-title">
                        Attendance Heatmap
                        <span className="hm-year-badge">{periodBadge}</span>
                    </h3>
                    <div className="hm-view-tabs">
                        {["year","month","week"].map(v => (
                            <button key={v} type="button"
                                className={`hm-view-tab${heatmapView === v ? " hm-view-tab--active" : ""}`}
                                onClick={() => setHeatmapView(v)}>
                                {v.charAt(0).toUpperCase() + v.slice(1)}
                            </button>
                        ))}
                    </div>
                </div>

                {/* ── Period controls + legend ── */}
                <div className="hm-controls-row">
                    <div className="hm-period-nav">
                        {heatmapView === "year" && (
                            <select className="hm-period-select" value={selectedYear} onChange={e => setSelectedYear(parseInt(e.target.value))}>
                                {[2026,2025,2024].map(y => <option key={y} value={y}>{y}</option>)}
                            </select>
                        )}
                        {heatmapView === "month" && (<>
                            <select className="hm-period-select" value={selectedYear} onChange={e => setSelectedYear(parseInt(e.target.value))}>
                                {[2026,2025,2024].map(y => <option key={y} value={y}>{y}</option>)}
                            </select>
                            <select className="hm-period-select" value={selectedMonth} onChange={e => setSelectedMonth(parseInt(e.target.value))}>
                                {MONTH_SHORT.map((m,i) => <option key={i} value={i}>{m}</option>)}
                            </select>
                        </>)}
                        {heatmapView === "week" && (<>
                            <button type="button" className="hm-week-nav-btn" onClick={() => stepWeek(-1)}><ChevronLeft size={15}/></button>
                            <span className="hm-week-range-label">{formatWeekRange(weekStart)}</span>
                            <button type="button" className="hm-week-nav-btn" onClick={() => stepWeek(1)}><ChevronRight size={15}/></button>
                        </>)}
                    </div>
                    <div className="hm-legend">
                        <div className="hm-legend-cell hm-bg-present"  /><span className="hm-legend-label">Present</span>
                        <span className="hm-legend-sep" />
                        <div className="hm-legend-cell hm-bg-overtime" /><span className="hm-legend-label">Overtime</span>
                        <span className="hm-legend-sep" />
                        <div className="hm-legend-cell hm-bg-absent"   /><span className="hm-legend-label">Absent</span>
                        <span className="hm-legend-sep" />
                        <div className="hm-legend-cell hm-bg-holiday"  /><span className="hm-legend-label">Holiday</span>
                    </div>
                </div>

                {/* ══ WEEK STRIP ══ */}
                {heatmapView === "week" && (
                    <div className="hm-week-strip">
                        {weekDays.map((day) => (
                            <div key={day.dateStr}
                                className={[
                                    "hm-week-day",
                                    day.isFuture          ? "hm-week-day--future"  : `hm-bg-${day.status}`,
                                    day.isToday           ? "hm-week-day--today"   : "",
                                    day.status==="weekend"? "hm-week-day--weekend" : "",
                                ].filter(Boolean).join(" ")}
                                onMouseEnter={!day.isFuture ? e => handleEnter(day, e) : undefined}
                                onMouseLeave={handleLeave}>
                                <span className="hm-week-day-name">{DAY_LABELS[day.dow]}</span>
                                <span className="hm-week-day-date">{day.day}</span>
                                <span className="hm-week-day-month">{MONTH_SHORT[day.month]}</span>
                                {!day.isFuture && <span className="hm-week-day-status">{STATUS_LABEL[day.status]}</span>}
                                {day.hours > 0 && <span className="hm-week-day-hrs">{day.hours}h</span>}
                            </div>
                        ))}
                    </div>
                )}

                {/* ══ MONTH CALENDAR ══ */}
                {heatmapView === "month" && (
                    <div className="hm-calendar">
                        {DAY_LABELS.map(d => (
                            <div key={d} className="hm-cal-header">{d}</div>
                        ))}
                        {monthWeeks.flat().map((day, i) => (
                            <div key={i}
                                className={[
                                    "hm-cal-cell",
                                    !day            ? "hm-cal-empty"   : "",
                                    day?.isFuture   ? "hm-cal-future"  : (day ? `hm-bg-${day.status}` : ""),
                                    day?.isToday    ? "hm-cal-today"   : "",
                                    day?.status === "weekend" ? "hm-cal-weekend" : "",
                                ].filter(Boolean).join(" ")}
                                onMouseEnter={day && !day.isFuture ? e => handleEnter(day, e) : undefined}
                                onMouseLeave={handleLeave}>
                                {day && (
                                    <>
                                        <span className="hm-cal-day-num">{day.day}</span>
                                        {!day.isFuture && day.status !== "weekend" && (
                                            <span className="hm-cal-status-dot" />
                                        )}
                                        {day.hours > 0 && (
                                            <span className="hm-cal-hrs">{day.hours}h</span>
                                        )}
                                    </>
                                )}
                            </div>
                        ))}
                    </div>
                )}

                {/* ══ YEAR CALENDAR (12 mini month calendars) ══ */}
                {heatmapView === "year" && (
                    <div className="hm-scroll">
                        <div className="hm-year-cal-row">
                            {yearCalMonths.map((mWeeks, mi) => (
                                <div key={mi} className="hm-year-cal-month">
                                    <div className="hm-year-cal-month-name">{MONTH_SHORT[mi]}</div>
                                    <div className="hm-year-cal-grid">
                                        {DAY_LABELS.map(d => (
                                            <div key={d} className="hm-year-cal-dh">{d[0]}</div>
                                        ))}
                                        {mWeeks.flat().map((day, i) => (
                                            <div key={i}
                                                className={[
                                                    "hm-year-cal-cell",
                                                    !day              ? "hm-cal-empty"   : "",
                                                    day?.isFuture     ? "hm-cal-future"  : (day ? `hm-bg-${day.status}` : ""),
                                                    day?.isToday      ? "hm-cal-today"   : "",
                                                    day?.status === "weekend" ? "hm-year-cal-weekend" : "",
                                                ].filter(Boolean).join(" ")}
                                                onMouseEnter={day && !day.isFuture ? e => handleEnter(day, e) : undefined}
                                                onMouseLeave={handleLeave}>
                                                {day && <span className="hm-year-cal-day-num">{day.day}</span>}
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>
                )}

                {tooltip && (
                    <div className="hm-tooltip" style={{ left: tooltip.x, top: tooltip.y }}>
                        <div className="hm-tooltip-date">{formatLong(tooltip.day.dateStr)}</div>
                        <div className="hm-tooltip-status">
                            <span className={`hm-tooltip-dot hm-bg-${tooltip.day.status}`} />
                            {STATUS_LABEL[tooltip.day.status]}
                        </div>
                        {tooltip.day.hours > 0 && (
                            <div className="hm-tooltip-meta"><Clock size={11}/> {tooltip.day.hours}h worked</div>
                        )}
                        {tooltip.day.lateMin > 0 && (
                            <div className="hm-tooltip-late">Late by {tooltip.day.lateMin} min</div>
                        )}
                    </div>
                )}
            </div>
        </div>
    );
}
